import { useEffect, useRef, useState } from 'react';

const SPARKS = [
  { top: '12%', left: '8%',  size: 3, delay: '0s',   dur: '5.2s' },
  { top: '22%', left: '18%', size: 2, delay: '1.1s',  dur: '6.5s' },
  { top: '8%',  left: '35%', size: 4, delay: '0.4s',  dur: '4.8s' },
  { top: '30%', left: '55%', size: 2, delay: '2s',    dur: '7s'   },
  { top: '15%', left: '72%', size: 3, delay: '0.8s',  dur: '5.5s' },
  { top: '40%', left: '85%', size: 2, delay: '1.5s',  dur: '6s'   },
  { top: '60%', left: '90%', size: 3, delay: '3s',    dur: '4.9s' },
  { top: '70%', left: '78%', size: 2, delay: '0.3s',  dur: '7.2s' },
  { top: '75%', left: '12%', size: 3, delay: '1.8s',  dur: '5.8s' },
  { top: '55%', left: '5%',  size: 2, delay: '2.5s',  dur: '6.3s' },
  { top: '45%', left: '28%', size: 2, delay: '3.5s',  dur: '5s'   },
  { top: '85%', left: '48%', size: 3, delay: '0.9s',  dur: '6.8s' },
];

const WA_CHANNEL = 'https://whatsapp.com/channel/0029VbCjXXHFcow1ohd1CY09';

const HeroSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const target = new Date('2026-04-11T16:00:00');
    const tick = () => {
      const now = new Date();
      const diff = target.getTime() - now.getTime();
      if (diff <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      ref={sectionRef}
      className="relative flex flex-col items-center overflow-hidden"
      style={{
        minHeight: '100svh',
        backgroundImage: `url('https://public.readdy.ai/ai/img_res/d3c9dfe0be405d1c32c543b10ed1a5d0.jpg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center top',
      }}
    >
      {/* ── Overlays ── */}
      <div className="absolute inset-0 bg-black/65" />
      <div
        className="absolute inset-0"
        style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.25) 35%, rgba(0,0,0,0.7) 70%, rgba(0,0,0,0.95) 100%)' }}
      />

      {/* ── Central radial glow ── */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse 70% 55% at 50% 38%, rgba(212,168,66,0.13) 0%, transparent 70%)' }}
      />

      {/* ── Stage beams ── */}
      {['-20deg','5deg','22deg','-8deg'].map((rot, i) => (
        <div
          key={i}
          className="absolute top-0 left-1/2 pointer-events-none anim-beam"
          style={{
            width: '2px',
            height: '65vh',
            background: `linear-gradient(to bottom, rgba(212,168,66,${0.22 - i * 0.03}) 0%, transparent 100%)`,
            transform: `translateX(${[-90,-20,40,110][i]}px) rotate(${rot})`,
            transformOrigin: 'top center',
            animationDelay: `${i * 1.2}s`,
          }}
        />
      ))}

      {/* ── Floating sparks ── */}
      {SPARKS.map((s, i) => (
        <div
          key={i}
          className="absolute rounded-full pointer-events-none anim-particle"
          style={{
            top: s.top,
            left: s.left,
            width: s.size,
            height: s.size,
            background: '#F0C040',
            boxShadow: `0 0 ${s.size * 3}px rgba(240,192,64,0.9)`,
            animationDuration: s.dur,
            animationDelay: s.delay,
          }}
        />
      ))}

      {/* ── Halo rings ── */}
      {[180, 240, 310].map((size, i) => (
        <div
          key={i}
          className="halo-ring pointer-events-none"
          style={{
            width: size,
            height: size,
            animationDelay: `${i * 1.3}s`,
            animationDuration: '4s',
            top: '38%',
          }}
        />
      ))}

      {/* ── Navbar ── */}
      <header
        className="absolute top-0 left-0 right-0 z-30 flex items-center justify-between px-5 py-4 md:px-10"
        style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.65), transparent)' }}
      >
        <img
          src="https://static.readdy.ai/image/d9bb5601c98f22586a2b11870925187d/71d6a8a551d583ad95743263bd4e0791.png"
          alt="SHAY Logo"
          className="h-11 w-auto object-contain"
          style={{ filter: 'drop-shadow(0 0 8px rgba(212,168,66,0.5))' }}
        />
        <a
          href={WA_CHANNEL}
          target="_blank"
          rel="nofollow noreferrer"
          className="whitespace-nowrap text-xs font-bold tracking-widest uppercase text-black px-5 py-2.5 rounded-full cursor-pointer transition-transform hover:scale-105"
          style={{
            background: 'linear-gradient(135deg, #F5D060, #D4A842)',
            boxShadow: '0 0 16px rgba(212,168,66,0.55)',
            fontFamily: "'Glacial Indifference', sans-serif",
          }}
        >
          Get Updates
        </a>
      </header>

      {/* ── Hero content ── */}
      <div
        className="relative z-20 flex flex-col items-center text-center px-5 w-full max-w-2xl mx-auto"
        style={{ paddingTop: '100px' }}
      >

        {/* 1. REVIVE NATION — Holy Midnight title font */}
        <h1
          className="font-black leading-none mb-4 anim-text-glow"
          style={{
            fontFamily: "'Holy Midnight', cursive",
            fontSize: 'clamp(3rem, 11vw, 6.5rem)',
            color: '#FFFFFF',
            letterSpacing: '0.01em',
            position: 'relative',
            zIndex: 21,
            textShadow: '0 0 40px rgba(0,0,0,0.9), 0 0 80px rgba(0,0,0,0.8)',
          }}
        >
          Revive Nation
        </h1>

        {/* 2. Badge */}
        <div
          className="mb-5 px-5 py-1.5 rounded-full text-xs font-bold tracking-widest uppercase"
          style={{
            background: 'rgba(212,168,66,0.13)',
            border: '1px solid rgba(212,168,66,0.5)',
            color: '#F5D060',
            fontFamily: "'Glacial Indifference', sans-serif",
            backdropFilter: 'blur(4px)',
          }}
        >
          ✦ A Youth Revival Gathering · April 2025 ✦
        </div>

        {/* 3. Gold divider */}
        <div className="flex items-center gap-3 mb-4 w-full" style={{ position: 'relative', zIndex: 21 }}>
          <div className="h-px flex-1" style={{ background: 'linear-gradient(to right, transparent, rgba(212,168,66,0.7))' }} />
          <div className="w-1.5 h-1.5 rounded-full anim-gold-pulse" style={{ background: '#D4A842', boxShadow: '0 0 10px rgba(212,168,66,1)' }} />
          <div className="h-px flex-1" style={{ background: 'linear-gradient(to left, transparent, rgba(212,168,66,0.7))' }} />
        </div>

        {/* 4. Ministers photo */}
        <div
          className="w-full flex justify-center"
          style={{ marginBottom: '-80px' }}
        >
          <div
            className="relative w-full"
            style={{
              maxWidth: '520px',
              WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 5%, black 72%, transparent 100%), linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%)',
              WebkitMaskComposite: 'destination-in',
              maskImage: 'linear-gradient(to bottom, transparent 0%, black 5%, black 72%, transparent 100%), linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%)',
              maskComposite: 'intersect',
            }}
          >
            <img
              src="https://static.readdy.ai/image/d9bb5601c98f22586a2b11870925187d/a2e4f299ce4b7bfc6e961f86909d0f42.png"
              alt="Conference Ministers — Ameeth Evans and team"
              className="w-full h-auto object-contain"
            />
          </div>
        </div>

        {/* 5. RECLAIM CONFERENCE — Bacalisties title font */}
        <h2
          className="font-black mb-5 whitespace-nowrap"
          style={{
            fontFamily: "'Bacalisties', cursive",
            fontSize: 'clamp(2.2rem, 9vw, 6rem)',
            color: '#D4A842',
            textShadow: '0 0 40px rgba(212,168,66,0.7), 0 0 80px rgba(212,168,66,0.35)',
            position: 'relative',
            zIndex: 21,
            letterSpacing: '0.04em',
            lineHeight: 1.1,
          }}
        >
          Reclaim Conference
        </h2>

        <p
          className="text-xl md:text-2xl font-black italic mb-4"
          style={{
            color: '#ECD9A0',
            fontFamily: "'Glacial Indifference', sans-serif",
            textShadow: '0 2px 12px rgba(0,0,0,0.8)',
            lineHeight: '2',
          }}
        >
          Reclaim the next generation.<br />
          Reclaim the fire.<br />
          Reclaim what belongs to God.
        </p>

        <p
          className="text-sm md:text-base mb-7"
          style={{
            color: 'rgba(220,210,190,0.78)',
            fontFamily: "'Glacial Indifference', sans-serif",
            maxWidth: '400px',
            lineHeight: '2.1',
          }}
        >
          Three Saturdays.<br />
          Three locations.<br />
          One cry —<br />
          that hearts would return to God and a generation would stand still and know that He is God.
        </p>

        {/* Date cards */}
        <div className="flex flex-nowrap justify-center gap-2 mb-6 w-full">
          {[
            { date: '11 April', loc: 'Arilova', mapUrl: 'https://maps.app.goo.gl/5SeBP2c5JtbELpTMA' },
            { date: '18 April', loc: 'Madhurawada', mapUrl: 'https://maps.app.goo.gl/o1wUS4msXwoj9RDy9' },
            { date: '25 April', loc: 'Yendada', mapUrl: 'https://maps.app.goo.gl/UrVabfG6nACTBy4m7' },
          ].map((item) => (
            <div
              key={item.loc}
              className="flex-1 min-w-0 px-2 py-3 rounded-xl text-center anim-border-shimmer"
              style={{ background: 'rgba(10,8,4,0.55)', backdropFilter: 'blur(10px)' }}
            >
              <div
                className="font-black tracking-widest mb-0.5 truncate"
                style={{ color: '#F5D060', fontFamily: "'Blinker', sans-serif", fontSize: 'clamp(0.7rem, 3.2vw, 0.9rem)' }}
              >
                {item.date}
              </div>
              <a
                href={item.mapUrl}
                target="_blank"
                rel="nofollow noreferrer"
                className="flex items-center justify-center gap-0.5 font-semibold cursor-pointer hover:opacity-75 transition-opacity truncate"
                style={{ fontFamily: "'Glacial Indifference', sans-serif", textDecoration: 'none', color: '#FFFFFF', fontSize: 'clamp(0.65rem, 2.8vw, 0.875rem)' }}
              >
                <div className="w-3 h-3 flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line" style={{ fontSize: '10px', color: '#D4A842' }} />
                </div>
                <span className="truncate">{item.loc}</span>
              </a>
            </div>
          ))}
        </div>

        {/* Time badge */}
        <div className="flex items-center gap-3 mb-7">
          <div className="h-px w-10" style={{ background: 'rgba(212,168,66,0.5)' }} />
          <div
            className="flex items-center gap-2 px-4 py-1.5 rounded-full"
            style={{ background: 'rgba(212,168,66,0.12)', border: '1px solid rgba(212,168,66,0.4)' }}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-time-line text-xs" style={{ color: '#F0C040' }} />
            </div>
            <span className="text-sm font-bold tracking-widest" style={{ color: '#F0C040', fontFamily: "'Blinker', sans-serif" }}>
              4:00 PM
            </span>
          </div>
          <div className="h-px w-10" style={{ background: 'rgba(212,168,66,0.5)' }} />
        </div>

        {/* Countdown Timer */}
        <div className="w-full mb-8">
          <p
            className="text-xs tracking-widest uppercase text-center mb-3"
            style={{ color: 'rgba(212,168,66,0.6)', fontFamily: "'Glacial Indifference', sans-serif" }}
          >
            Starts in
          </p>
          <div className="grid grid-cols-4 gap-2 w-full">
            {[
              { value: timeLeft.days,    label: 'Days' },
              { value: timeLeft.hours,   label: 'Hours' },
              { value: timeLeft.minutes, label: 'Mins' },
              { value: timeLeft.seconds, label: 'Secs' },
            ].map((unit) => (
              <div
                key={unit.label}
                className="flex flex-col items-center justify-center py-3 rounded-2xl"
                style={{
                  background: 'rgba(10,8,4,0.6)',
                  border: '1px solid rgba(212,168,66,0.3)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <span
                  className="font-black leading-none mb-1"
                  style={{
                    fontFamily: "'Blinker', sans-serif",
                    fontSize: 'clamp(1.6rem, 7vw, 2.4rem)',
                    color: '#F5D060',
                    textShadow: '0 0 20px rgba(212,168,66,0.6)',
                  }}
                >
                  {String(unit.value).padStart(2, '0')}
                </span>
                <span
                  className="text-xs uppercase tracking-widest"
                  style={{ color: 'rgba(212,168,66,0.55)', fontFamily: "'Glacial Indifference', sans-serif" }}
                >
                  {unit.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <button
            onClick={() => scrollTo('conference-dates')}
            className="whitespace-nowrap px-9 py-4 rounded-full font-black text-sm tracking-widest uppercase text-black cursor-pointer transition-all hover:scale-105"
            style={{
              background: 'linear-gradient(135deg, #F5D060 0%, #D4A842 50%, #A07820 100%)',
              fontFamily: "'Glacial Indifference', sans-serif",
              boxShadow: '0 0 30px rgba(212,168,66,0.65), 0 0 60px rgba(212,168,66,0.25), inset 0 1px 0 rgba(255,255,255,0.3)',
            }}
          >
            Join the Conference
          </button>
          <a
            href={WA_CHANNEL}
            target="_blank"
            rel="nofollow noreferrer"
            className="whitespace-nowrap px-9 py-4 rounded-full font-bold text-sm tracking-widest uppercase cursor-pointer transition-all hover:scale-105"
            style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1.5px solid rgba(212,168,66,0.65)',
              color: '#F5D060',
              fontFamily: "'Glacial Indifference', sans-serif",
              backdropFilter: 'blur(8px)',
              boxShadow: '0 0 18px rgba(212,168,66,0.15)',
            }}
          >
            Stay Connected
          </a>
        </div>
      </div>

      {/* ── Bottom glow ── */}
      <div
        className="absolute bottom-0 left-0 right-0 pointer-events-none"
        style={{
          height: '220px',
          background: 'radial-gradient(ellipse 80% 100% at 50% 100%, rgba(212,168,66,0.1) 0%, transparent 70%)',
          zIndex: 17,
        }}
      />
    </section>
  );
};

export default HeroSection;
